import { useMemo } from 'react';
import { useGetAssetsAvailabilityDataQuery } from '../api/booking/bookingApiSlice';
import { useGetAccountQuery } from '../api/account/accountApiSlice';
import { useTenantProductCategoriesQuery } from './useTenantProductCategoriesQuery';
import { useViewedTenantAccount } from './useViewedTenantAccount';
import type { AccountTenant } from '../api/account/accountApiTypes';
import type { ProductCategory } from '../api/productCategory/productCategoryApiSlice';
import {
    bankDetailsRequiredForPayments,
    hasPaymentRouteForChargeableUseCases,
    hasSavedServicesConfig,
} from '../pages/services/serviceConfigurationRules';

export interface SetupSteps {
    createVenue: boolean;
    /**
     * The tenant has SAVED a service configuration — booking logic, charging
     * and, where something is charged, the payment arrangement.
     *
     * The decisive step of the whole checklist, and the one it never named:
     * every step below depends on its answers (whether an IBAN is needed at
     * all, how a reservation is settled), and the Services form itself says
     * "Noch ist nichts gespeichert" until the first save turns the proposed
     * baseline into a decision.
     */
    servicesConfigured: boolean;
    bankDetails: boolean;
    contactInfo: boolean;
    legalNotice: boolean;
    bookingPage: boolean;
}

export interface SetupProgress {
    stepsComplete: SetupSteps;
    /**
     * Whether this tenant needs an IBAN on file — i.e. some chargeable offer
     * is settled by bank transfer, or by an invoice kolula issues (the invoice
     * prints the bank details; PO, 2026-10-19). False for a tenant whose offers
     * are all free, AND for one invoicing from its own system (`external`): its
     * payment requests never come from kolula, so the checklist must not demand
     * details it can never use.
     *
     * Named after what it gates rather than after "payment", because those are
     * not the same question — an externally invoicing tenant charges money and
     * still needs no IBAN.
     */
    bankDetailsRequired: boolean;
    /** Every step that applies to this tenant is done. */
    setupComplete: boolean;
    /**
     * Whether the tenant has progressed far enough through the setup
     * checklist to start accepting bookings: an object exists, contact and
     * legal notice are set, and — only if something is actually charged for —
     * a payment route is in place. Booking-management surfaces (sidebar entry,
     * etc.) gate on this for self-service tenants.
     */
    canCreateBookings: boolean;
}

/**
 * Computes which setup-checklist steps are complete and whether the
 * tenant can start creating bookings.
 *
 * Sources:
 *   - `createVenue`   ← `useGetAssetsAvailabilityDataQuery` (≥1 asset).
 *                       Same endpoint the assets page uses, so RTK
 *                       Query shares the cache and an asset created via
 *                       `AssetCreatePage` flips this step automatically
 *                       (the create-asset mutation invalidates the
 *                       `assetAvailabilityData` tag).
 *   - `bankDetails`   ← `useGetAccountQuery` → `tenant.bank` non-null.
 *                       Saved via PATCH /admin/account/bank-details.
 *   - `contactInfo`   ← `useGetAccountQuery` → `tenant.contact` non-null.
 *                       Saved via PATCH /admin/account/contact. Its own
 *                       step since #1956: public contact used to be
 *                       written alongside the bank object, so a non-null
 *                       `bank` implied it. Splitting the endpoints made
 *                       the section skippable, and every room/venue
 *                       customer email renders this contact — an unset
 *                       one ships a support paragraph with two empty
 *                       links. Gating booking creation on it is the
 *                       front half of the fix; the back half stops the
 *                       backend resolving a missing value to the demo
 *                       tenant's contact.
 *   - `legalNotice`   ← `useGetAccountQuery` → `tenant.legalNotice`
 *                       non-null. Saved via
 *                       PATCH /admin/account/legal-notice.
 *   - `bookingPage`   ← `useGetAccountQuery` → `tenant.endUserAppActive`.
 *                       Toggled via PATCH /admin/account/booking-page from
 *                       the account page's booking-page section.
 *
 * Both account-backed steps update automatically when the user saves
 * via the AccountPage: every PATCH mutation in `accountApiSlice`
 * invalidates the `Account` tag, refetching the cache.
 */
export function useSetupProgress(): SetupProgress & { assetsFailed: boolean } {
    const assets = useCurrentAssets();
    const { data: account } = useGetAccountQuery();

    const tenant =
        account && 'tenant' in account ? account.tenant : null;

    // Categories may settle differently from their use case (ADR-0026 §U), so
    // the payment questions below count their overrides too. Only operational
    // tenants have categories — the endpoint refuses legacy ones — so the read
    // is gated on the tenant ON SCREEN (a master may be viewing a legacy one),
    // not on the caller's own account. Live rows only: an archived category
    // takes no bookings.
    const { data: categories } = useTenantProductCategoriesQuery();

    // `assetsFailed`: the asset read errored, so `createVenue` (and everything
    // built on it) is not known. A reader shows that instead of a guessed
    // "no object" (CLAUDE.md, "A Failure Must Never Look Like an Absence").
    return { ...setupProgressOf(tenant, assets.data?.length ?? 0, categories ?? []), assetsFailed: assets.isError };
}

/** The asset statistics of the current month — the `createVenue` source. */
const useCurrentAssets = () => {
    const now = useMemo(() => new Date(), []);
    return useGetAssetsAvailabilityDataQuery({
        data: {
            year: String(now.getFullYear()),
            month: String(now.getMonth() + 1),
        },
    });
};

/**
 * Where the checklist of the tenant in view stands. `failed` is its own state,
 * never a checklist: a read that did not answer must not look like a step that
 * is done or one that is missing (CLAUDE.md, "A Failure Must Never Look Like an
 * Absence").
 */
export type ViewedSetupProgress =
    | { state: 'loading' }
    | { state: 'failed' }
    | { state: 'ready', progress: SetupProgress };

/**
 * The same checklist for the tenant IN VIEW (`useViewedTenantAccount`), not the
 * caller's own: `useSetupProgress` reads a bare `useGetAccountQuery()`, which
 * for a master on another tenant answers the master's own tenant (#1334,
 * #2059). Assets and categories already travel with the viewed tenant's
 * `X-Tenant` header. `loading` while any of the three reads has not answered,
 * `failed` when one of them errored (or the account read settled without a
 * tenant), so no step is guessed — neither a Kritisch `no_room` from an
 * unanswered asset read nor a bank severity from unanswered categories.
 *
 * Only the onboarding import uses it today; the Dashboard's checklist is hidden
 * for masters and the booking-page toggle only for owners, so both keep
 * `useSetupProgress`.
 */
export function useViewedSetupProgress(): ViewedSetupProgress {
    const assets = useCurrentAssets();
    const { accountTenant, isResolving, hasFailed } = useViewedTenantAccount({ withPaymentIntegration: false });
    const categories = useTenantProductCategoriesQuery();
    if (hasFailed || assets.isError || categories.isError) return { state: 'failed' };
    if (isResolving || assets.isLoading || categories.isLoading) return { state: 'loading' };
    if (!accountTenant) return { state: 'failed' };
    return { state: 'ready', progress: setupProgressOf(accountTenant, assets.data?.length ?? 0, categories.data ?? []) };
}

/** The checklist's rules, for any tenant. ONE place: both hooks above call it. */
export function setupProgressOf(
    tenant: AccountTenant | null | undefined,
    assetCount: number,
    categories: ProductCategory[],
): SetupProgress {
    const stepsComplete: SetupSteps = {
        createVenue: assetCount >= 1,
        // Saved means decided: nothing the tenant saved is stored until they
        // save the Services form once. Entries our template stored on
        // switch-on do not count — the tenant has not decided anything yet
        // (`hasSavedServicesConfig`). Deliberately not "does it look sensible":
        // the form's own validator refuses an incomplete arrangement on save,
        // so anything stored has already passed it.
        servicesConfigured: hasSavedServicesConfig(tenant?.payments),
        bankDetails: !!tenant?.bank,
        contactInfo: !!tenant?.contact,
        legalNotice: !!tenant?.legalNotice,
        // True once the tenant activates its end-user booking page
        // (tenant.config.endUserAppActive), via the account page's
        // booking-page section.
        bookingPage: tenant?.endUserAppActive === true,
    };

    const payment = hasPaymentRouteForChargeableUseCases(
        tenant?.payments,
        {
            bankDetails: !!tenant?.bank?.iban,
            invoiceIssuer: tenant?.invoiceIssuer,
        },
        categories,
    );

    const canCreateBookings =
        stepsComplete.createVenue &&
        stepsComplete.contactInfo &&
        stepsComplete.legalNotice &&
        payment.satisfied;

    // The bank step only counts towards "done" for a tenant whose payment
    // requests carry the bank details: a bank transfer, or an invoice kolula
    // issues (PO, 2026-10-19). A free tenant's checklist could never complete
    // otherwise — and neither could the one of a tenant invoicing from its own
    // system, which would be told to add an IBAN it never uses, permanently.
    const bankDetailsRequired = bankDetailsRequiredForPayments(
        tenant?.payments,
        categories,
        tenant?.invoiceIssuer,
    );

    /*
     * `servicesConfigured` is deliberately NOT a condition here, and the
     * reason is worth stating because it looks like an omission.
     *
     * `bookingPage` is already required, so adding it would change the outcome
     * in exactly one case: a tenant that is ALREADY LIVE and never saved a
     * Services configuration. For them the checklist would reappear on the
     * dashboard months after onboarding — a nag about a form whose absence
     * changes nothing today (an unconfigured tenant falls back to legacy
     * behaviour by design, see the Services wiring sequence).
     *
     * The step still does its job: the checklist is visible for exactly as
     * long as the tenant is not live, which is precisely when configuring
     * Services matters.
     */
    const setupComplete =
        canCreateBookings
        && stepsComplete.bookingPage
        && (!bankDetailsRequired || stepsComplete.bankDetails);

    return {
        stepsComplete,
        bankDetailsRequired,
        setupComplete,
        canCreateBookings,
    };
}
